/**
 * PR 9.2k(owner 红线): 数据完整性 gate helper。
 *
 * 9.2g 三态:`ready`(recompute 完成有 metrics) / `data_not_ready`(周已结束但 metrics 未生成)。
 * 未就绪的周**绝不**显 0 chart/table(会误导业务下滑),改 placeholder。
 */

export type WeekStatus = 'ready' | 'data_not_ready';

export const DATA_NOT_READY_HINT = '数据采集中，预计本周一 03:10 CST recompute 后展示';

/** week_status 是否就绪。undefined 视为 ready(向后兼容旧 API / 非 week-scoped 响应)。 */
export function isWeekDataReady(status?: WeekStatus): boolean {
  return status !== 'data_not_ready';
}

/** 给定 history(近 N 周 + status),返回应在周选择器里 disable 的周集合。 */
export function notReadyWeekSet(
  history: ReadonlyArray<{ week: string; status: WeekStatus }>,
): Set<string> {
  return new Set(history.filter((h) => h.status === 'data_not_ready').map((h) => h.week));
}

/* ============================================================
 * PR 9.2m: /models/weekly envelope + Agent readiness/S-tree helpers
 * ============================================================ */

/** PR 9.2m: `/api/v2/models/weekly` 由 array 改为 envelope(top-level week_status)。 */
export interface ModelsWeeklyEnvelope<T> {
  week: string;
  week_status: WeekStatus;
  models: T[];
}

/** envelope → models 数组(carry-forward 1:Models.tsx + BrandDetail.tsx 共用,旧 array stub 作废)。 */
export function modelsFromEnvelope<T>(resp: ModelsWeeklyEnvelope<T> | undefined): T[] {
  return resp?.models ?? [];
}

/** Agent not-ready 409 错误体(`{code, week_status, missing_reasons}`)。 */
export interface AgentNotReadyDetail {
  code: 'WEEK_DATA_NOT_READY';
  week_status: WeekStatus;
  missing_reasons: string[];
}

/** missing_reasons 机读枚举 → 中文友好文案。 */
export const MISSING_REASON_LABELS: Record<string, string> = {
  NO_COMPLAINT_FACTS: '本周尚未采集到投诉数据',
  ALL_ZERO_WEEKLY_METRICS: '本周投诉周指标全为 0',
  NO_FACT_ROWS: '本周尚无任何事实数据',
  SOURCE_RUNS_FAILED: '本周数据源抓取失败',
  WEEK_NOT_COMPLETED: '本周尚未结束',
  NO_SUMMARY: '本周摘要尚未生成',
};

/** 把 missing_reasons 拼成一句友好提示(W23 红线:不显编造分析,只说采集状态)。 */
export function missingReasonsText(reasons: readonly string[] | undefined): string {
  if (!reasons || reasons.length === 0) return DATA_NOT_READY_HINT;
  const parts = reasons.map((r) => MISSING_REASON_LABELS[r] ?? r);
  return `${DATA_NOT_READY_HINT}（${parts.join('；')}）`;
}

/** axios 错误是否为 Agent not-ready 409(409 + code=WEEK_DATA_NOT_READY)。 */
export function isAgentNotReadyError(err: unknown): AgentNotReadyDetail | null {
  const e = err as { response?: { status?: number; data?: { detail?: AgentNotReadyDetail } } };
  const detail = e?.response?.data?.detail;
  if (e?.response?.status === 409 && detail?.code === 'WEEK_DATA_NOT_READY') {
    return detail;
  }
  return null;
}

/** S-tree markdown 正文一段(标题 + 正文)。 */
export interface StreeSection {
  heading: string;
  body: string;
}

/** 把 summary_md(`## 标题\n正文`)解析为分段,供 Agent 折叠展示。 */
export function parseStreeSections(md: string | null | undefined): StreeSection[] {
  if (!md) return [];
  const out: StreeSection[] = [];
  const parts = md.split(/^##\s+/m);
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const nl = trimmed.indexOf('\n');
    if (nl === -1) {
      out.push({ heading: trimmed, body: '' });
    } else {
      out.push({ heading: trimmed.slice(0, nl).trim(), body: trimmed.slice(nl + 1).trim() });
    }
  }
  return out;
}
